package ai.wagemix.data.firebase

import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMMixFilters
import ai.wagemix.net.models.WMOwnedShare
import ai.wagemix.net.models.WMSharedMixPreviewStatus
import ai.wagemix.net.models.WMSharedMixPurpose
import ai.wagemix.net.models.WMStoredMix
import ai.wagemix.net.models.WMSubscriptionInfo
import ai.wagemix.net.models.WMUpload
import ai.wagemix.net.models.WMShareStats
import ai.wagemix.net.models.WMUploadFailureReason
import ai.wagemix.net.models.WMUploadSource
import ai.wagemix.net.models.WMUploadStatus
import ai.wagemix.net.models.firebase.KxsWMFirebaseMix
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.WMUserData
import ai.wagemix.net.models.firebase.WMUserRole
import ai.wagemix.net.models.firebase.WMUserStatus
import ai.wagemix.net.models.firebase.of
import ai.wagemix.net.models.of
import com.fsryan.foundation.models.toEnumValue
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/*
 * The Firestore document shapes the site reads, decoded with the gitlive SDK's serializer
 * support, and their mapping onto the domain interfaces. Firestore is the only place these
 * shapes exist; nothing above this module sees them.
 */

internal fun Timestamp.toInstant(): Instant = Instant.fromEpochSeconds(seconds, nanoseconds.toLong())

/** The two domain values one `users/{uid}` document carries. */
internal class WMUserDocumentValue(val userData: WMUserData, val subscription: WMSubscriptionInfo)

/** `users/{uid}` */
@Serializable
internal data class UserDocument(
    val deleted: Boolean = false,
    val email: String = "",
    val firstName: String? = null,
    val lastName: String? = null,
    val role: String = WMUserRole.User.stringValue,
    val stripeCustomerId: String? = null,
    val status: String = WMUserStatus.Created.stringValue,
    val subscriptionType: String = WMSubscriptionType.None.stringValue,
    val updatedAt: Timestamp? = null,
    // Written by the Stripe webhook handlers.
    val subscriptionInterval: String? = null,
    val subscriptionCurrentPeriodEnd: Timestamp? = null,
    val subscriptionCancelAtPeriodEnd: Boolean = false
) {
    fun toDomain(): WMUserDocumentValue {
        val type = subscriptionType.toEnumValue(WMSubscriptionType.None)
        return WMUserDocumentValue(
            userData = WMUserData.of(
                deleted = deleted,
                email = email,
                firstName = firstName,
                lastName = lastName,
                role = role.toEnumValue(WMUserRole.User),
                stripeCustomerId = stripeCustomerId,
                status = status.toEnumValue(WMUserStatus.Created),
                subscriptionType = type,
                updatedAt = updatedAt?.toInstant() ?: Instant.DISTANT_PAST
            ),
            subscription = WMSubscriptionInfo.of(
                plan = type,
                interval = subscriptionInterval,
                currentPeriodEnd = subscriptionCurrentPeriodEnd?.toInstant(),
                cancelAtPeriodEnd = subscriptionCancelAtPeriodEnd
            )
        )
    }
}

/** The `filters` map of a stored mix. */
@Serializable
internal data class MixFiltersDocument(val areaId: Int? = null, val industryId: Int? = null) {
    fun toDomain(): WMMixFilters = WMMixFilters.of(areaId, industryId)
}

/** `users/{uid}/storedMixes/{id}` */
@Serializable
internal data class StoredMixDocument(
    val title: String = "",
    val occupations: List<KxsWMFirebaseMix.Occupation> = emptyList(),
    val isDeleted: Boolean = false,
    val fileId: String? = null,
    val createdAt: Timestamp? = null,
    val modifiedAt: Timestamp? = null,
    val filters: MixFiltersDocument? = null
) {
    fun toDomain(id: String): WMStoredMix = WMStoredMix.of(
        id = id,
        title = title,
        occupations = occupations,
        fileId = fileId,
        createdAt = createdAt?.toInstant(),
        modifiedAt = modifiedAt?.toInstant(),
        filters = filters?.toDomain() ?: WMMixFilters.none
    )
}

/** `users/{uid}/sharedMixStats/{sharedMixId}`: a share link's clicks, counted by the API; readable on Pro only. */
@Serializable
internal data class ShareStatsDocument(
    val clickCount: Long = 0,
    val deactivatedClickCount: Long = 0,
    val lastClickedAt: Timestamp? = null
) {
    fun toDomain(sharedMixId: String): WMShareStats {
        return WMShareStats.of(sharedMixId, clickCount, deactivatedClickCount, lastClickedAt?.toInstant())
    }
}

/** `users/{uid}/sharedMixes/{sharedMixId}`: the owner's record of a share, written by the API. */
@Serializable
internal data class OwnedShareDocument(
    val mixId: String? = null,
    val title: String = "",
    val badge: String = WMBadge.None.stringValue,
    val mode: String = "annual",
    val purpose: String = WMSharedMixPurpose.Social.stringValue,
    val shareableUrl: String = "",
    val createdAt: Timestamp? = null,
    val previewStatus: String? = null,
    val previewImageUrl: String? = null,
    val previewRequestedAt: Timestamp? = null,
    /** Absent on records from before links could be turned off: those are on. */
    val active: Boolean = true,
    /** Absent on shares from before link statistics were added: those have none. */
    val statsTracked: Boolean = false
) {
    fun toDomain(sharedMixId: String): WMOwnedShare = WMOwnedShare.of(
        sharedMixId = sharedMixId,
        mixId = mixId,
        title = title,
        badge = badge.toEnumValue(WMBadge.None),
        hourly = mode == "hourly",
        purpose = purpose.toEnumValue(WMSharedMixPurpose.Unknown),
        shareableUrl = shareableUrl,
        createdAt = createdAt?.toInstant(),
        previewStatus = previewStatus?.let { value ->
            WMSharedMixPreviewStatus.entries.firstOrNull { it.stringValue == value }
        },
        previewImageUrl = previewImageUrl,
        previewRequestedAt = previewRequestedAt?.toInstant(),
        active = active,
        statsTracked = statsTracked
    )
}

/** `users/{uid}/uploads/{id}` */
@Serializable
internal data class UploadDocument(
    val classification: String = "jd",
    val contentType: String = "",
    val originalFileName: String = "",
    val size: Long = 0,
    val uploadedAt: Timestamp? = null,
    // Written by the pipeline; absent on older documents.
    val status: String? = null,
    val statusMessage: String? = null,
    val source: String? = null,
    val sourceUrl: String? = null,
    val mixId: String? = null,
    val statusUpdatedAt: Timestamp? = null,
    val failureReason: String? = null
) {
    /**
     * [mixIdsByFileId] is the stored-mixes join for documents written before the pipeline
     * recorded `status` and `mixId`: an upload with a mix is ready, one without is still processing.
     */
    fun toDomain(id: String, mixIdsByFileId: Map<String, String>): WMUpload {
        val resolvedMixId = mixId ?: mixIdsByFileId[id]
        val resolvedStatus = status?.toEnumValue(WMUploadStatus.Unknown)
            ?: if (resolvedMixId != null) WMUploadStatus.Ready else WMUploadStatus.Processing
        val resolvedSource = source?.toEnumValue(WMUploadSource.Unknown)
            ?: if (sourceUrl != null) WMUploadSource.Url else WMUploadSource.File
        return WMUpload.of(
            id = id,
            originalFileName = originalFileName,
            contentType = contentType,
            size = size,
            uploadedAt = uploadedAt?.toInstant(),
            status = resolvedStatus,
            statusMessage = statusMessage,
            source = resolvedSource,
            sourceUrl = sourceUrl,
            mixId = resolvedMixId,
            statusUpdatedAt = statusUpdatedAt?.toInstant(),
            failureReason = failureReason?.toEnumValue(WMUploadFailureReason.Unknown)
        )
    }
}
